<template>
  <b-container>
    <section
    :class="`booking-engine booking-engine--flight ${
      customizations.glass ? 'booking-engine--glass' : ''
    } ${customizations.inline ? 'booking-engine--inline' : ''}
     ${
       customizations.hide_location_search
         ? 'booking-engine--items-2'
         : 'booking-engine--items-3'
     } ${is_open ? 'booking-engine--is-open' : ''}`"
    ref="booking-engine"
  >
    <!-- Options row: trip type + passengers -->
    <b-row class="booking-engine__options">
      <b-col cols="12" class="booking-engine__options__trip">
        <trip-type-selector
          v-model="trip_type"
          :max_origin_destinations="max_origin_destinations"
        />
        <div class="booking-engine__checkbox">
          <search-by-connections />
        </div>
      </b-col>
    </b-row>

    <!-- Form row: airports + dates + search -->
    <b-row class="booking-engine__fields">
      <!-- Origin & Destination (Departure, Arrival, Date picker) -->
      <b-col
        class="booking-engine__origin-destinations"
        cols="12"
        :lg="trip_type === TRIP_TYPES.MULTI_ITINERARY ? 12 : 9"
      >
        <origin-destination
          v-for="(origin_destination, origin_destination_index) in visible_origin_destinations"
          :key="`origin_destination:${origin_destination_index}`"
          :departure="origin_destination.departure"
          :arrival="origin_destination.arrival"
          :dates="get_origin_destination_dates(origin_destination, is_round_trip)"
          :origin_destination_index="origin_destination_index"
          :origin_destination_count="origin_destinations.length"
          :return_flight="is_round_trip"
          :date_constraints="get_date_constraints(origin_destination_index)"
          @update:departure="(update) => on_update_departure(origin_destination_index, update)"
          @update:arrival="(update) => on_update_arrival(origin_destination_index, update)"
          @update:dates="(update) => on_update_dates(origin_destination_index, update)"
          @airport-focus="scroll_into_view"
          @remove="on_remove_origin_destination"
          @invert="on_invert_origin_destination"
        />
        <add-origin-destination
          v-if="trip_type === TRIP_TYPES.MULTI_ITINERARY"
          :disabled="origin_destinations.length >= max_origin_destinations"
          @add:origin-destination="on_add_origin_destination"
        />
      </b-col>
      <b-col cols="12" :lg="trip_type === TRIP_TYPES.MULTI_ITINERARY ? 12 : 3">
        <!-- Booking Search Controls (Occupancy + Search button) -->
        <booking-search-controls
          :full_width="trip_type === TRIP_TYPES.MULTI_ITINERARY"
          @search="handle_search_submit_button"
          @occupancy-change="on_occupancy_change"
        />
      </b-col>
    </b-row>
  </section>
  </b-container>
</template>

<script>
import SearchByConnections from "@liveflight/components/blocks/filters/search-by-connections";
import TripTypeSelector from "./trip-type-selector.vue";
import OriginDestination from "./origin-destination.vue";
import BookingSearchControls from "./booking-search-controls.vue";
import AddOriginDestination from "./add-origin-destination.vue";
import {
  TRIP_TYPES,
  update_origin_destination_by_index,
  remove_origin_destination_by_index,
  add_origin_destination,
  flip_origin_destination,
  create_origin_destination,
  get_next_date,
  is_origin_destination_valid,
} from "@liveflight/utilities/origin-destination";
import { is_mobile, is_authenticated, get_config } from "@/utilities/";
import { remove_time_from_date, toDate, toDateString } from "@liveflight/utilities/dates";
import { calculateMinDepartureDate } from "./logic";
import { reverse_date } from "@/utilities/commons";

export default {
  name: "liveflight-booking-engine",
  components: {
    "add-origin-destination": AddOriginDestination,
    "trip-type-selector": TripTypeSelector,
    "origin-destination": OriginDestination,
    "booking-search-controls": BookingSearchControls,
    "search-by-connections": SearchByConnections,
  },
  props: {
    // Overrides constraints other than base logic
    date_constraints: {
      type: Object,
      default: () => ({}),
    },
    // Override base style customizations for specific scenarios
    customizations: {
      type: Object,
      default: () => ({}),
    },
    is_open: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    // Origin-destinations array (read-only, managed by child component)
    origin_destinations: {
      get() {
        return this.$store.state.liveflight.origin_destinations;
      },
      set(value) {
        this.$store.commit("liveflight/origin_destinations", value);
      },
    },
    // Return flight flag (read-only, managed by child component)
    is_round_trip() {
      return this.trip_type === TRIP_TYPES.ROUND_TRIP;
    },
    // Travelers (read-only, managed by child component)
    travelers() {
      return this.$store.state.liveflight.travelers;
    },
    // Convenience getters for validation in mounted hook
    departure_date() {
      const od = this.origin_destinations[0];
      return od && od.date ? od.date : "";
    },
    return_date() {
      const od = this.origin_destinations[1];
      return od && od.date ? od.date : "";
    },
    departure_min_date() {
      return (
        this.date_constraints.departure_min_date || calculateMinDepartureDate()
      );
    },
    // Trip type computed property (maps boolean return_flight to TRIP_TYPES enum)
    trip_type: {
      get() {
        return this.$store.state.liveflight.trip_type;
      },
      set(value) {
        this.$store.commit("liveflight/set_trip_type", value);
      },
    },
    max_origin_destinations() {
      return this.get_config("guiSettings.searchMaxOriginDestinations", 2);
    },
    can_search_suggestions: {
      get() {
        return this.$store.state.liveflight.can_search_suggestions;
      },
      set(value) {
        this.$store.commit("liveflight/can_search_suggestions", value);
      },
    },
    visible_origin_destinations() {
      return this.is_round_trip
        ? [this.origin_destinations[0]]
        : this.origin_destinations;
    },
  },
  data() {
    return {
      config: this.$config,
      request: {},
      TRIP_TYPES,
    };
  },
  created() {
    this.is_authenticated = is_authenticated;
    this.reverse_date = reverse_date;
    this.is_mobile = is_mobile;
    this.remove_time_from_date = remove_time_from_date;
  },
  mounted() {
    window.eventBus.$off("trigger-search-by-suggestion");
    window.eventBus.$on("trigger-search-by-suggestion", () => {
      this.handle_trigger_search();
    });

    window.eventBus.$off("change-search-by");
    window.eventBus.$on("change-search-by", () => {
      if (this.$route.name !== "results") return;
      this.handle_trigger_search();
    });

    // Validate and adjust dates on mount for ALL legs
    const min_departure = toDateString(this.departure_min_date);
    const updated = [...this.origin_destinations];
    let needs_update = false;

    for (let i = 0; i < updated.length; i++) {
      const current_date = updated[i].date || "";
      const min_date = i === 0 ? min_departure : updated[i - 1].date;

      if (!current_date || current_date < min_date) {
        updated[i] = { ...updated[i], date: min_date };
        needs_update = true;
      }
    }

    if (needs_update) {
      this.$store.commit("liveflight/origin_destinations", updated);
    }
  },
  methods: {
    get_date_constraints(origin_destination_index) {
      if (origin_destination_index < 1) {
        return this.date_constraints;
      }
      const { date: previous } =
        this.origin_destinations[origin_destination_index - 1] || {};
      const departure_min_date = previous
        ? toDate(previous)
        : new Date();
      return { ...this.date_constraints, departure_min_date };
    },
    get_origin_destination_dates(origin_destination, is_round_trip) {
      if (!is_round_trip) {
        return [origin_destination.date];
      }

      const { date: departure_date = "" } = this.origin_destinations[0] || {};
      const { date: return_date = "" } = this.origin_destinations[1] || {};
      return [departure_date, return_date];
    },
    scroll_into_view() {
      if (this.$refs && this.$refs["booking-engine"]) {
        this.$refs["booking-engine"].scrollIntoView({ behavior: "smooth" });
      }
    },
    on_update_departure(index, location) {
      const updated = update_origin_destination_by_index(
        this.origin_destinations,
        index,
        { departure: location },
        this.is_round_trip,
      );
      this.$store.commit("liveflight/origin_destinations", updated);
    },
    on_update_arrival(index, location) {
      const updated = update_origin_destination_by_index(
        this.origin_destinations,
        index,
        { arrival: location },
        this.is_round_trip,
      );
      this.$store.commit("liveflight/origin_destinations", updated);
    },
    on_update_dates(index, dates) {
      // For ROUND_TRIP, dates = [departure_date, return_date]
      // For ONE_WAY/MULTI, dates = [departure_date]
      let updated = [...this.origin_destinations];

      updated[index] = { ...updated[index], date: dates[0] };
      if (
        this.trip_type === TRIP_TYPES.ROUND_TRIP &&
        dates.length === 2 &&
        updated[1]
      ) {
        // Update second origin-destination (return leg) with return date
        updated[1] = { ...updated[1], date: dates[1] };
      }

      this.$store.commit("liveflight/origin_destinations", updated);
    },
    on_remove_origin_destination(index) {
      const updated = remove_origin_destination_by_index(
        this.origin_destinations,
        index,
      );
      this.$store.commit("liveflight/origin_destinations", updated);
    },
    on_invert_origin_destination(index) {
      const updated = [...this.origin_destinations];
      updated[index] = flip_origin_destination(updated[index]);
      if (this.trip_type === TRIP_TYPES.ROUND_TRIP && updated[1]) {
        updated[1] = flip_origin_destination(updated[1]);
      }
      this.$store.commit("liveflight/origin_destinations", updated);
    },
    on_occupancy_change() {
      // Event from booking-search-controls component when occupancy changes
    },
    on_add_origin_destination() {
      // Start from last origin_destination
      const { arrival: previous_arrival, date: previous_date } =
        this.origin_destinations[this.origin_destinations.length - 1];

      // Add the new one to the existing array
      const new_origin_destinations = add_origin_destination(
        this.origin_destinations,
        create_origin_destination({
          departure: previous_arrival,
          date: get_next_date(previous_date),
        }),
      );

      // Commit to the store
      this.$store.commit(
        "liveflight/origin_destinations",
        new_origin_destinations,
      );
    },
    handle_search_submit_button() {
      this.can_search_suggestions = true;
      this.$store.commit("liveflight/suggestions", {});
      this.search_flights();
    },
    handle_trigger_search() {
      this.search_flights();
    },
    create_search_request() {
      // Build origin destinations from store
      const originDestinations = this.origin_destinations
        .filter(is_origin_destination_valid)
        .map(({ departure, arrival, date }, i) => ({
          id: String(i + 1),
          originLocationCode: departure.iata_code,
          destinationLocationCode: arrival.iata_code,
          departureDateTimeRange: {
            date: date,
          },
        }));

      const request = {
        currencyCode: "EUR",
        originDestinations,
        travelers: this.travelers,
        sources: ["GDS"],
        searchCriteria: {
          addOneWayOffers: false,
          flightFilters: {
            returnToDepartureAirport: this.is_round_trip,
            connectionRestriction: {
              maxNumberOfConnections:
                this.$store.state.liveflight.max_number_of_connections,
            },
          },
          pricingOptions: {
            includedCheckedBagsOnly:
              this.$store.state.liveflight.included_checked_bags_only == 1,
          },
          additionalInformation: {
            chargeableCheckedBags: true,
          },
          allowAlternativeFareOptions: get_config(
            "searchSettings.allowAlternativeFareOptions",
            false,
          ),
        },
      };

      if (
        ["ECONOMY", "PREMIUM_ECONOMY", "BUSINESS", "FIRST"].includes(
          this.$store.state.liveflight.cabin,
        )
      ) {
        request.searchCriteria.flightFilters.cabinRestrictions = [
          {
            cabin: this.$store.state.liveflight.cabin,
            coverage: "ALL_SEGMENTS",
            originDestinationIds: originDestinations.map((od) => od.id),
          },
        ];
      }

      return request;
    },
    search_flights() {
      const request = this.create_search_request();
      this.request = request;
      this.$store.commit("liveflight/initiate_search", request);

      // Emit search event for parent to handle redirect (redirect-then-search pattern)
      this.$emit("search");
    },
  },
};
</script>

<style lang="scss">
.booking-engine--flight {
  position: relative;
  z-index: 100;
  max-width: 1175px;
  margin: 0 auto;
  background: $white;
  padding: $grid-gutter-20;
  border-radius: $border-radius-lg * 2;
  border: 1px solid $white;
  box-shadow: 0 3px 12px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.08);

  @include media-breakpoint-down(md) {
    display: none;

    @at-root .homepage-view & {
      display: block;
    }
  }

  &.booking-engine--is-open {
    display: block;

    @include media-breakpoint-down(md) {
      margin-top: $grid-gutter-20;
      position: absolute;
      left: $grid-gutter-width;
      right: $grid-gutter-width;
      box-shadow: 0 0 15px 0 rgba($dark, .2);
    }
  }

  &--glass {
    background-color: rgba($extra2, 0.75);
  }
  &--inline {
    margin: 0;
  }
  &--items-2 {
    max-width: 800px;
  }

  .booking-engine__origin-destinations > * {
    @for $i from 1 through 10 {
      &:nth-child(#{$i}) {
        z-index: 11 - $i;
      }
    }
  }

  @include media-breakpoint-up(lg) {
    & > .row {
      & > *:nth-child(3),
      & > *:nth-child(4),
      & > *:nth-child(5),
      & > *:nth-child(6) {
        padding-left: 0;
      }
    }
  }

  // Options row
  &.booking-engine .booking-engine__options {
    margin-bottom: $grid-gutter-20;

    &__trip {
      display: flex;
      align-items: center;
      gap: $grid-gutter-20;
    }
  }

  // Form group
  .form-group {
    margin-bottom: 0;
  }
}
</style>
